<template>
  <div>
    <DetailSheetHeader
      :title="title"
      :subtitle="subtitle"
      :icon-url="iconUrl"
      :icon-alt="prettyType"
    />

    <div class="waypoint-details">
      <DetailSheetSection v-if="photos.length" plain>
        <DetailSheetPhotoStrip :photos="photos" @open="openLightbox" />
      </DetailSheetSection>

      <DetailSheetSection v-if="description" plain>
        <p class="waypoint-description">{{ displayedDescription }}</p>
        <button
          v-if="descriptionNeedsTruncation"
          type="button"
          class="btn btn-link btn-sm p-0 see-more-toggle"
          @click="descriptionExpanded = !descriptionExpanded"
        >
          {{ descriptionExpanded ? $t('waypoint_dialog.view_waypoint_details.see_less') : $t('waypoint_dialog.view_waypoint_details.see_more') }}
        </button>
      </DetailSheetSection>

      <DetailSheetSection>
        <DetailSheetActionRow
          v-if="isUsersWaypoint"
          :label="$t('shared.edit_details')"
          icon="edit"
          @click="editWaypointDetails"
        />
        <DetailSheetActionRow
          v-if="isInPlanTrail && point"
          :label="$t('waypoint_dialog.view_waypoint_details.include_on_route')"
          icon="add_location_alt"
          @click="includeOnRoute"
        />
        <DetailSheetActionRow
          v-if="point"
          :label="$t('waypoint_dialog.view_waypoint_details.plan_a_hike_from_here')"
          icon="explore"
          @click="planTrailToWaypoint"
        />
        <DetailSheetActionRow
          v-if="point"
          :label="$t('waypoint_dialog.view_waypoint_details.driving_directions')"
          icon="directions_car"
          @click="getDirectionsToWaypoint"
        />
        <DetailSheetActionRow
          v-if="point"
          :label="$t('waypoint_dialog.view_waypoint_details.view_on_google_street_view')"
          icon="streetview"
          pro
          @click="openOnGoogleStreetView"
        />
        <DetailSheetActionRow
          :label="$t('waypoint_dialog.view_waypoint_details.print_map_of_waypoint')"
          icon="print"
          @click="printWaypoint"
        />
        <DetailSheetActionRow
          v-if="selectedWaypointShareUrl"
          :label="$t('waypoint_dialog.view_waypoint_details.share_waypoint')"
          icon="share"
          :hint="selectedWaypointShareUrl"
          :status="showCopied ? $t('common.copied') : ''"
          @click="shareClicked"
        />
        <DetailSheetActionRow
          v-if="isUsersWaypoint"
          :label="$t('waypoint_dialog.view_waypoint_details.delete_waypoint')"
          icon="delete"
          danger
          @click="deleteWaypoint"
        />
      </DetailSheetSection>

      <DetailSheetSection>
        <DetailSheetMetaRow
          v-if="preferenceLonLat"
          :label="$t('shared.coords')"
          :value="String(preferenceLonLat)"
          copyable
          :copied="showCopiedCoords"
          @copy="copyCoordinates"
        />
        <DetailSheetMetaRow v-if="prettyType" :label="$t('shared.type_2')" :value="prettyType" />
        <DetailSheetMetaRow v-if="formattedDate" :label="$t('waypoint_dialog.view_waypoint_details.created')" :value="formattedDate" />
        <DetailSheetMetaRow v-if="clientId" label="ID" :value="String(clientId)" />
      </DetailSheetSection>
    </div>

    <DetailSheetLightbox
      :open="lightboxOpen"
      :photos="photos"
      :index="lightboxIndex"
      @close="lightboxOpen = false"
      @prev="showPrevPhoto"
      @next="showNextPhoto"
    />
  </div>
</template>

<script>

import { Modal } from 'bootstrap';
import { defineComponent } from 'vue';
import { mapGetters, mapState } from 'vuex';
import DetailSheetActionRow from '../../../utils/shared_components/detail_sheet/DetailSheetActionRow.vue';
import DetailSheetHeader from '../../../utils/shared_components/detail_sheet/DetailSheetHeader.vue';
import DetailSheetLightbox from '../../../utils/shared_components/detail_sheet/DetailSheetLightbox.vue';
import DetailSheetMetaRow from '../../../utils/shared_components/detail_sheet/DetailSheetMetaRow.vue';
import DetailSheetPhotoStrip from '../../../utils/shared_components/detail_sheet/DetailSheetPhotoStrip.vue';
import DetailSheetSection from '../../../utils/shared_components/detail_sheet/DetailSheetSection.vue';
import { detailSheetSubtitle, formatDetailTimestamp } from '../../../utils/helpers/detailSheetFormat';
import { GridRefUtils } from '../../../utils/helpers/GridRefPreference';
import { googleStreetViewUrl, openGoogleMaps } from '../../../utils/helpers/OpenOnGoogleMaps';
import { clampNextIndex, clampPrevIndex } from '../../../utils/helpers/photoViewerNav';
import { truncateAtWord, needsTruncation } from '../../../utils/helpers/truncateText';
import { waypointPhotos } from '../../../utils/helpers/waypointDetails';
import { wktPointToLatLng } from '../../../utils/helpers/WKTPointToLatLng';
import { POIUtility } from '../../../utils/models/POI';
import { WaypointOptionsDialogPages } from '../../../utils/models/WaypointOptionsDialogPages';
import { RevenueCatWrapper } from '../../../utils/RevenueCatWrapper';

const DESCRIPTION_TRUNCATE_LIMIT = 300;
const COPIED_FEEDBACK_MS = 2000;

export default defineComponent({
  components: {
    DetailSheetActionRow,
    DetailSheetHeader,
    DetailSheetLightbox,
    DetailSheetMetaRow,
    DetailSheetPhotoStrip,
    DetailSheetSection,
  },
  data() {
    return {
      gridRefUtils: null,
      showCopied: false,
      showCopiedCoords: false,
      descriptionExpanded: false,
      lightboxOpen: false,
      lightboxIndex: 0,
    };
  },
  mounted() {
    this.gridRefUtils = GridRefUtils.preference();
  },
  computed: Object.assign(mapState({
    selectedWaypoint: (state) => state.waypoints.selectedWaypoint,
  }), mapGetters({
    selectedWaypointShareUrl: 'waypoints/selectedWaypointShareUrl',
  }), {
    id() {
      return this.selectedWaypoint.id;
    },
    title() {
      return this.selectedWaypoint.title || this.$t('waypoint_dialog.view_waypoint_details.waypoint');
    },
    type() {
      return this.selectedWaypoint.type;
    },
    prettyType() {
      try {
        return POIUtility.fromString(this.type).prettyName();
      } catch (e) {
        return this.type || '';
      }
    },
    iconUrl() {
      try {
        return POIUtility.fromString(this.type).getPublicImageIcon();
      } catch (e) {
        return POIUtility.fromString('other').getPublicImageIcon();
      }
    },
    subtitle() {
      return detailSheetSubtitle([this.prettyType, this.formattedDate]);
    },
    description() {
      return this.selectedWaypoint.description;
    },
    descriptionNeedsTruncation() {
      return needsTruncation(this.description, DESCRIPTION_TRUNCATE_LIMIT);
    },
    displayedDescription() {
      if (this.descriptionExpanded) return this.description || '';
      return truncateAtWord(this.description, DESCRIPTION_TRUNCATE_LIMIT);
    },
    lonlat() {
      return this.selectedWaypoint.lonlat;
    },
    preferenceLonLat() {
      return this.gridRefUtils && this.point ? this.gridRefUtils.convert(this.point) : this.lonlat;
    },
    point() {
      return this.lonlat ? wktPointToLatLng(this.lonlat) : null;
    },
    createdAt() {
      return this.selectedWaypoint.createdAt;
    },
    updatedAt() {
      return this.selectedWaypoint.updatedAt;
    },
    clientId() {
      return this.selectedWaypoint.clientId;
    },
    userId() {
      return this.selectedWaypoint.userId;
    },
    isUsersWaypoint() {
      if (window.user == null) {
        return false;
      }
      return this.selectedWaypoint.userId === window.user.id;
    },
    isInPlanTrail() {
      return !!this.$store.state.planTrail;
    },
    public() {
      return this.selectedWaypoint.public;
    },
    formattedDate() {
      return formatDetailTimestamp(this.createdAt);
    },
    photos() {
      return waypointPhotos(this.selectedWaypoint);
    },
  }),
  methods: {
    openLightbox(index) {
      this.lightboxIndex = index;
      this.lightboxOpen = true;
    },
    showPrevPhoto() {
      this.lightboxIndex = clampPrevIndex(this.lightboxIndex);
    },
    showNextPhoto() {
      this.lightboxIndex = clampNextIndex(this.lightboxIndex, this.photos.length);
    },
    async copyText(text, flag) {
      try {
        await navigator.clipboard.writeText(text);
        this[flag] = true;
        window.setTimeout(() => { this[flag] = false; }, COPIED_FEEDBACK_MS);
      } catch (err) {
        console.error('Unable to copy text to clipboard', err);
      }
    },
    async openOnGoogleStreetView() {
      const isPro = await RevenueCatWrapper.isProOrProPlus();
      if (!isPro) {
        window.location.href = '/purchases';
        return;
      }

      window.open(googleStreetViewUrl(this.point.lat, this.point.lng), '_blank');
    },
    editWaypointDetails() {
      this.$store.commit('waypoints/setTopPage', WaypointOptionsDialogPages.WaypointEditDetails);
    },
    deleteWaypoint() {
      this.$store.dispatch('waypoints/deleteSelectedWaypoint');
    },
    shareClicked() {
      if (this.selectedWaypointShareUrl) {
        this.copyText(this.selectedWaypointShareUrl, 'showCopied');
      }
    },
    copyCoordinates() {
      if (this.preferenceLonLat) {
        this.copyText(String(this.preferenceLonLat), 'showCopiedCoords');
      }
    },
    getDirectionsToWaypoint() {
      openGoogleMaps(this.point.lat, this.point.lng);
    },
    planTrailToWaypoint() {
      this.$store.dispatch('waypoints/planTrailToSelectedWaypoint');
    },
    async includeOnRoute() {
      if (!this.point) return;
      if (typeof window.canEditReference === 'function' && !window.canEditReference()) {
        if (typeof window.checkReferenceEditWarning === 'function') {
          const confirmed = await window.checkReferenceEditWarning();
          if (!confirmed) return;
        } else {
          return;
        }
      }
      // The row used to carry data-bs-dismiss; now that it is a shared component
      // the dialog is closed explicitly before the marker lands on the map.
      const element = document.getElementById('waypoint-options-dialog');
      if (element) Modal.getInstance(element)?.hide();

      this.$store.dispatch('planTrail/addMarkerAtCoordinates', {
        lng: this.point.lng,
        lat: this.point.lat,
      });
    },
    printWaypoint() {
      window.location.href = `/print-maps?data_ids[]=${this.selectedWaypoint.id}&data_types[]=Waypoint`;
    },
  },
});
</script>

<style scoped>
.waypoint-details {
  padding: 1rem;
}

.waypoint-description {
  margin: 0;
  white-space: pre-line;
  color: #fff;
  font-size: 0.9375rem;
  line-height: 1.55;
}

.see-more-toggle {
  color: var(--yellow);
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
}

.see-more-toggle:hover {
  color: var(--yellow);
  text-decoration: underline;
}
</style>
